import { Button } from '@curtin-dxp/curtin-ui';
import { Column } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronsLeftRightIcon } from 'lucide-react';
import { HTMLAttributes } from 'react';
import { cn, formatHours } from '@/lib/utils';

interface DataTableColumnHeaderProps<TData, TValue> extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  sortable?: boolean;
  className?: string;
  titleClassName?: string;
  columnTotal?: number;
}

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
  sortable = false,
  className,
  titleClassName,
  columnTotal,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  const renderSortIcon = () => {
    const sort = column.getIsSorted();
    if (!sort) {
      return <ChevronsLeftRightIcon className="ml-2 h-4 w-4 rotate-90" />;
    }
    return sort === 'desc' ? (
      <ArrowDownIcon className="ml-2 h-4 w-4" stroke="#4a4a4a" />
    ) : (
      <ArrowUpIcon className="ml-2 h-4 w-4" stroke="#4a4a4a" />
    );
  };

  return (
    <div className={className}>
      {sortable ? (
        <Button
          variant="mute"
          className={cn(
            `border-0 px-[10px] text-base font-semibold text-secondary-grey-01 focus:outline-none focus-visible:ring-1
              focus-visible:ring-inset focus-visible:ring-deep-blue focus-visible:ring-offset-0
              ${sortable ? 'cursor-pointer' : 'cursor-default'} }`,
            titleClassName,
          )}
          onClick={sortable ? column.getToggleSortingHandler() : undefined}
          aria-label={title + (sortable ? ' sortable' : '')}
        >
          <span className={titleClassName}>{title}</span>
          {sortable ? renderSortIcon() : ''}
        </Button>
      ) : (
        <>
          <div className={cn('border-0 px-[10px] text-base font-semibold text-secondary-grey-02', titleClassName)}>
            {title}
          </div>

          {columnTotal !== undefined && columnTotal >= 0 ? (
            <div className="text-b3 font-bold text-primary-black-90">{formatHours(columnTotal)}</div>
          ) : (
            ''
          )}
        </>
      )}
    </div>
  );
};
